import React, { useState, useMemo } from 'react';
import {
  UserCheck,
  User,
  Users,
  Calendar,
  DollarSign,
  Download,
  FileSpreadsheet,
  FileText,
  CreditCard,
  Banknote,
  Landmark,
  Wrench,
  CheckCircle2,
  Clock,
  Search,
  Filter,
  ArrowRight,
  TrendingUp,
  Package,
  Layers,
} from 'lucide-react';
import { AppUser, CompanyInfo, ServiceTicket, PaymentMethod } from '../types';
import {
  calculateTicketTotal,
  calculateTicketPartsCost,
  formatTL,
  formatDateTR,
  STATUS_LABELS,
  STATUS_COLORS,
  getTicketPaymentSplit,
  getTicketPaymentSplitForPeriod,
  formatTicketPaymentMethodDisplay,
  isTicketAssignedToUser,
  getTicketAssignedTechnicianDisplay,
  getStoredUsers,
  getTicketReportDate,
} from '../utils/storage';
import { exportTechnicianReportPDF } from '../utils/pdfGenerator';
import { exportTechnicianReportExcel } from '../utils/excelGenerator';

interface TechnicianReportSectionProps {
  tickets: ServiceTicket[];
  companyInfo: CompanyInfo;
  currentUser?: AppUser | null;
  selectedMonth: string;
  onMonthChange: (month: string) => void;
  availableMonths: string[];
  selectedTechId?: string;
  onTechChange?: (techId: string) => void;
}

const MONTH_NAMES_TR: Record<string, string> = {
  '01': 'Ocak',
  '02': 'Şubat',
  '03': 'Mart',
  '04': 'Nisan',
  '05': 'Mayıs',
  '06': 'Haziran',
  '07': 'Temmuz',
  '08': 'Ağustos',
  '09': 'Eylül',
  '10': 'Ekim',
  '11': 'Kasım',
  '12': 'Aralık',
};

export const TechnicianReportSection: React.FC<TechnicianReportSectionProps> = ({
  tickets,
  companyInfo,
  currentUser,
  selectedMonth,
  onMonthChange,
  availableMonths,
  selectedTechId: propSelectedTechId,
  onTechChange,
}) => {
  const isTechnicianUser = currentUser?.role === 'technician';

  // Load all registered users
  const allUsers = useMemo(() => {
    return getStoredUsers().filter(u => u.isActive !== false);
  }, []);

  // Filter technicians list (technician role users or all staff)
  const technicianUsers = useMemo(() => {
    const techs = allUsers.filter(u => u.role === 'technician');
    return techs.length > 0 ? techs : allUsers;
  }, [allUsers]);

  // Selected technician ID
  const [internalTechId, setInternalTechId] = useState<string>(() => {
    if (isTechnicianUser && currentUser) {
      return currentUser.id || currentUser.username;
    }
    return technicianUsers[0]?.id || 'all';
  });

  const selectedTechId = propSelectedTechId !== undefined ? propSelectedTechId : internalTechId;
  const setSelectedTechId = onTechChange || setInternalTechId;

  const [tableFilter, setTableFilter] = useState<'all' | 'delivered' | 'active' | 'cash' | 'card' | 'transfer' | 'debt'>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Active technician object
  const activeTechnician = useMemo(() => {
    if (isTechnicianUser && currentUser) return currentUser;
    if (selectedTechId === 'all') return null;
    return allUsers.find(u => u.id === selectedTechId || u.username === selectedTechId) || null;
  }, [isTechnicianUser, currentUser, selectedTechId, allUsers]);

  const activeTechnicianDisplayName = useMemo(() => {
    if (isTechnicianUser && currentUser) return currentUser.name || currentUser.username;
    if (activeTechnician) return activeTechnician.name || activeTechnician.username;
    if (selectedTechId === 'all') return 'Tüm Teknisyenler (Genel Döküm)';
    return selectedTechId;
  }, [isTechnicianUser, currentUser, activeTechnician, selectedTechId]);

  const formattedMonthName = useMemo(() => {
    if (selectedMonth === 'all') return 'Tüm Zamanlar';
    try {
      const [year, month] = selectedMonth.split('-');
      return `${MONTH_NAMES_TR[month] || month} ${year}`;
    } catch {
      return selectedMonth;
    }
  }, [selectedMonth]);

  // Filter tickets by selected month & selected technician
  const filteredTickets = useMemo(() => {
    return tickets.filter(t => {
      // Gizli kayıtlar ve sözleşmeli işler teknisyen kazanç raporuna girmez.
      if (t.isDeleted) return false;
      if (t.serviceType === 'Sozlesmeli' || Boolean(t.contractedCompanyId)) return false;

      // 1. Month filter
      if (selectedMonth !== 'all') {
        const reportDate = getTicketReportDate(t);
        const inServiceMonth = Boolean(reportDate && reportDate.startsWith(selectedMonth));
        const hasPeriodPayment = getTicketPaymentSplitForPeriod(t, selectedMonth).total > 0;
        if (!inServiceMonth && !hasPeriodPayment) return false;
      }

      // 2. Technician filter
      if (isTechnicianUser && currentUser) {
        return isTicketAssignedToUser(t, currentUser);
      }

      if (selectedTechId === 'all') {
        return true;
      }

      if (activeTechnician) {
        return isTicketAssignedToUser(t, activeTechnician);
      }

      // Fallback matching by ID or name
      const hasRepairTechnician = Boolean(t.repairedByUser || t.repairedByName);
      if (hasRepairTechnician) {
        return (
          t.repairedByUser === selectedTechId ||
          t.repairedByName === selectedTechId
        );
      }
      return (
        t.assignedTechnicianId === selectedTechId ||
        t.assignedTechnicianUsername === selectedTechId ||
        t.assignedTechnicianName === selectedTechId
      );
    });
  }, [tickets, selectedMonth, isTechnicianUser, currentUser, selectedTechId, activeTechnician]);

  // Calculate high-impact performance & financial metrics
  const metrics = useMemo(() => {
    let totalLaborRevenue = 0;
    let totalPartsRevenue = 0;
    let totalPartsCost = 0;
    let totalSubCost = 0;
    let totalTurnover = 0;
    let totalCollected = 0;
    let totalRemaining = 0;

    let totalCash = 0;
    let totalCard = 0;
    let totalTransfer = 0;

    let deliveredCount = 0;
    let activeCount = 0;
    let returnedCount = 0;

    filteredTickets.forEach(t => {
      const { subtotal, vatAmount, partsTotal, grandTotal } = calculateTicketTotal(
        t.parts,
        t.laborCost,
        t.vatRate,
        t.discount,
        t.isVatIncluded, t.roundingAdjustment || 0
      );
      const partsCost = calculateTicketPartsCost(t.parts);
      const subCost = t.subServiceCost || 0;
      const periodSplit = selectedMonth === 'all' ? getTicketPaymentSplit(t) : getTicketPaymentSplitForPeriod(t, selectedMonth);
      const paid = periodSplit.total;
      const paidRatio = grandTotal > 0 ? paid / grandTotal : 0;
      const reportDate = getTicketReportDate(t);
      const serviceInPeriod = selectedMonth === 'all' || Boolean(reportDate && reportDate.startsWith(selectedMonth));

      totalTurnover += paid;
      totalLaborRevenue += (t.laborCost || 0) * paidRatio;
      totalPartsRevenue += (partsTotal || 0) * paidRatio;
      if (serviceInPeriod) {
        totalPartsCost += partsCost;
        totalSubCost += subCost;
      }

      if (t.status === 'TeslimEdildi') {
        deliveredCount++;
      } else if (t.status === 'IadeEdildi' || t.status === 'IadeEdilecek') {
        returnedCount++;
      } else {
        activeCount++;
      }

      const remaining = Math.max(0, grandTotal - getTicketPaymentSplit(t).total);
      totalCollected += paid;
      totalRemaining += remaining;

      totalCash += periodSplit.cash;
      totalCard += periodSplit.card;
      totalTransfer += periodSplit.transfer;
    });

    const netEarnings = totalTurnover - totalPartsCost - totalSubCost;

    return {
      totalTickets: filteredTickets.length,
      deliveredCount,
      activeCount,
      returnedCount,
      totalLaborRevenue,
      totalPartsRevenue,
      totalPartsCost,
      totalSubCost,
      totalTurnover,
      totalCollected,
      totalRemaining,
      totalCash,
      totalCard,
      totalTransfer,
      netEarnings,
    };
  }, [filteredTickets]);

  // Table rows with search & category filters
  const tableRows = useMemo(() => {
    return filteredTickets.filter(t => {
      const { grandTotal } = calculateTicketTotal(
        t.parts,
        t.laborCost,
        t.vatRate,
        t.discount,
        t.isVatIncluded, t.roundingAdjustment || 0
      );
      const split = selectedMonth === 'all' ? getTicketPaymentSplit(t) : getTicketPaymentSplitForPeriod(t, selectedMonth);
      const paid = split.total;
      const remaining = Math.max(0, grandTotal - getTicketPaymentSplit(t).total);

      // Filter by chip
      if (tableFilter === 'delivered' && t.status !== 'TeslimEdildi') return false;
      if (tableFilter === 'active' && (t.status === 'TeslimEdildi' || t.status === 'IadeEdildi')) return false;
      if (tableFilter === 'cash' && split.cash <= 0) return false;
      if (tableFilter === 'card' && split.card <= 0) return false;
      if (tableFilter === 'transfer' && split.transfer <= 0) return false;
      if (tableFilter === 'debt' && remaining <= 0) return false;

      // Filter by search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const matchesId = t.id.toLowerCase().includes(q);
        const matchesCustomer = (t.customerName || '').toLowerCase().includes(q) || (t.customerPhone || '').includes(q);
        const matchesDevice = `${t.deviceBrand || ''} ${t.deviceModel || ''}`.toLowerCase().includes(q);
        const matchesNote = (t.technicianNotes || '').toLowerCase().includes(q) || (t.issueDescription || '').toLowerCase().includes(q);
        if (!matchesId && !matchesCustomer && !matchesDevice && !matchesNote) {
          return false;
        }
      }

      return true;
    });
  }, [filteredTickets, tableFilter, searchQuery]);

  return (
    <div className="space-y-6">
      {/* 5 Main KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* 1. Baktığı Cihaz Sayısı */}
        <div className="bg-white rounded-2xl p-4.5 border border-slate-200/80 shadow-sm space-y-1.5">
          <div className="text-slate-500 text-xs font-semibold flex items-center justify-between">
            <span>Baktığı Toplam Cihaz</span>
            <span className="p-1 rounded-md bg-blue-50 text-blue-600">
              <Wrench className="w-3.5 h-3.5" />
            </span>
          </div>
          <h3 className="text-2xl font-black text-slate-900">
            {metrics.totalTickets} <span className="text-sm font-semibold text-slate-500">Cihaz</span>
          </h3>
          <div className="flex items-center justify-between text-[11px] text-slate-500 pt-1 border-t border-slate-100">
            <span className="text-emerald-700 font-bold">✓ {metrics.deliveredCount} Teslim Edildi</span>
            <span className="text-blue-700 font-bold">{metrics.activeCount} İşlemde</span>
          </div>
        </div>

        {/* 2. Toplam Servis Cirosu & İşçilik */}
        <div className="bg-white rounded-2xl p-4.5 border border-slate-200/80 shadow-sm space-y-1.5">
          <div className="text-slate-500 text-xs font-semibold flex items-center justify-between">
            <span>Toplam Servis Cirosu</span>
            <span className="p-1 rounded-md bg-indigo-50 text-indigo-600">
              <DollarSign className="w-3.5 h-3.5" />
            </span>
          </div>
          <h3 className="text-2xl font-black text-indigo-700">{formatTL(metrics.totalTurnover)}</h3>
          <div className="flex items-center justify-between text-[11px] text-slate-500 pt-1 border-t border-slate-100">
            <span>İşçilik Geliri:</span>
            <span className="font-bold text-slate-900">{formatTL(metrics.totalLaborRevenue)}</span>
          </div>
        </div>

        {/* 3. Nakit Tahsilat (Kasa) */}
        <div className="bg-white rounded-2xl p-4.5 border border-slate-200/80 shadow-sm space-y-1.5">
          <div className="text-slate-500 text-xs font-semibold flex items-center justify-between">
            <span>Nakit Tahsilat (Kasa)</span>
            <span className="p-1 rounded-md bg-emerald-50 text-emerald-600">
              <Banknote className="w-3.5 h-3.5" />
            </span>
          </div>
          <h3 className="text-2xl font-black text-emerald-600">{formatTL(metrics.totalCash)}</h3>
          <div className="flex items-center justify-between text-[11px] text-slate-500 pt-1 border-t border-slate-100">
            <span>Toplam Tahsilat:</span>
            <span className="font-bold text-emerald-700">{formatTL(metrics.totalCollected)}</span>
          </div>
        </div>

        {/* 4. Kredi Kartı & EFT/Havale */}
        <div className="bg-white rounded-2xl p-4.5 border border-slate-200/80 shadow-sm space-y-1.5">
          <div className="text-slate-500 text-xs font-semibold flex items-center justify-between">
            <span>Kart & Banka Tahsilatı</span>
            <span className="p-1 rounded-md bg-purple-50 text-purple-600">
              <CreditCard className="w-3.5 h-3.5" />
            </span>
          </div>
          <div className="space-y-0.5">
            <div className="flex items-baseline justify-between">
              <span className="text-xs text-slate-600 font-medium">Kredi Kartı:</span>
              <span className="text-sm font-bold text-purple-700">{formatTL(metrics.totalCard)}</span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-xs text-slate-600 font-medium">EFT / Havale:</span>
              <span className="text-sm font-bold text-sky-700">{formatTL(metrics.totalTransfer)}</span>
            </div>
          </div>
          <div className="flex items-center justify-between text-[11px] text-slate-500 pt-1 border-t border-slate-100">
            <span>Kalan Borç:</span>
            <span className="font-bold text-amber-700">{formatTL(metrics.totalRemaining)}</span>
          </div>
        </div>

        {/* 5. Parça Maliyeti & Net Hakediş */}
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 text-white rounded-2xl p-4.5 shadow-md space-y-1.5 relative overflow-hidden border border-slate-700">
          <div className="text-slate-300 text-xs font-semibold flex items-center justify-between">
            <span>Net Servis Kazancı</span>
            <span className="p-1 rounded-md bg-slate-800 text-emerald-400">
              <TrendingUp className="w-3.5 h-3.5" />
            </span>
          </div>
          <h3 className="text-2xl font-black text-emerald-400 tracking-tight">
            {formatTL(metrics.netEarnings)}
          </h3>
          <div className="pt-1.5 border-t border-slate-700/80 flex items-center justify-between text-xs text-slate-300">
            <span className="text-[11px] text-rose-300">Parça: -{formatTL(metrics.totalPartsCost)}</span>
            {metrics.totalSubCost > 0 && (
              <span className="text-[11px] text-amber-300">Alt Servis: -{formatTL(metrics.totalSubCost)}</span>
            )}
          </div>
        </div>
      </div>

      {/* Payment Method Breakdown Bar */}
      <div className="bg-white p-4.5 rounded-2xl border border-slate-200/80 shadow-sm space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
            <CreditCard className="w-4 h-4 text-indigo-600" />
            <span>Tahsilat Şekline Göre Kasa Dağılımı ({formattedMonthName})</span>
          </h4>
          <span className="text-xs font-bold text-slate-800">
            Toplam Tahsil Edilen: <b className="text-emerald-600">{formatTL(metrics.totalCollected)}</b>
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Nakit */}
          <div className="bg-emerald-50/60 border border-emerald-200 rounded-xl p-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-lg bg-emerald-600 text-white shadow-xs">
                <Banknote className="w-4 h-4" />
              </div>
              <div>
                <span className="text-xs text-emerald-950 font-bold block">Nakit Tahsilat</span>
                <span className="text-[11px] text-emerald-700">Elden / Kasa Girişi</span>
              </div>
            </div>
            <div className="text-right">
              <span className="text-sm font-extrabold text-emerald-700 block">{formatTL(metrics.totalCash)}</span>
              <span className="text-[10px] font-bold text-emerald-800 bg-emerald-100 px-1.5 py-0.5 rounded">
                %{metrics.totalCollected > 0 ? Math.round((metrics.totalCash / metrics.totalCollected) * 100) : 0}
              </span>
            </div>
          </div>

          {/* Kredi Kartı */}
          <div className="bg-purple-50/60 border border-purple-200 rounded-xl p-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-lg bg-purple-600 text-white shadow-xs">
                <CreditCard className="w-4 h-4" />
              </div>
              <div>
                <span className="text-xs text-purple-950 font-bold block">Kredi Kartı</span>
                <span className="text-[11px] text-purple-700">POS Cihazı / Kart</span>
              </div>
            </div>
            <div className="text-right">
              <span className="text-sm font-extrabold text-purple-700 block">{formatTL(metrics.totalCard)}</span>
              <span className="text-[10px] font-bold text-purple-800 bg-purple-100 px-1.5 py-0.5 rounded">
                %{metrics.totalCollected > 0 ? Math.round((metrics.totalCard / metrics.totalCollected) * 100) : 0}
              </span>
            </div>
          </div>

          {/* EFT / Havale */}
          <div className="bg-sky-50/60 border border-sky-200 rounded-xl p-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-lg bg-sky-600 text-white shadow-xs">
                <Landmark className="w-4 h-4" />
              </div>
              <div>
                <span className="text-xs text-sky-950 font-bold block">Hesaba (EFT/Havale)</span>
                <span className="text-[11px] text-sky-700">Banka Transferi</span>
              </div>
            </div>
            <div className="text-right">
              <span className="text-sm font-extrabold text-sky-700 block">{formatTL(metrics.totalTransfer)}</span>
              <span className="text-[10px] font-bold text-sky-800 bg-sky-100 px-1.5 py-0.5 rounded">
                %{metrics.totalCollected > 0 ? Math.round((metrics.totalTransfer / metrics.totalCollected) * 100) : 0}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Device Table Section */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden space-y-0">
        {/* Table Controls & Filter Bar */}
        <div className="p-4 border-b border-slate-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-slate-50/70">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs font-bold text-slate-700 mr-1 flex items-center gap-1">
              <Filter className="w-3.5 h-3.5 text-slate-500" />
              Filtrele:
            </span>
            <button
              onClick={() => setTableFilter('all')}
              className={`px-2.5 py-1 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                tableFilter === 'all'
                  ? 'bg-slate-900 text-white shadow-xs'
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
              }`}
            >
              Tümü ({filteredTickets.length})
            </button>
            <button
              onClick={() => setTableFilter('delivered')}
              className={`px-2.5 py-1 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                tableFilter === 'delivered'
                  ? 'bg-emerald-600 text-white shadow-xs'
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
              }`}
            >
              Teslim Edilenler ({metrics.deliveredCount})
            </button>
            <button
              onClick={() => setTableFilter('active')}
              className={`px-2.5 py-1 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                tableFilter === 'active'
                  ? 'bg-blue-600 text-white shadow-xs'
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
              }`}
            >
              İşlemde ({metrics.activeCount})
            </button>
            <button
              onClick={() => setTableFilter('cash')}
              className={`px-2.5 py-1 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                tableFilter === 'cash'
                  ? 'bg-emerald-700 text-white shadow-xs'
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
              }`}
            >
              Nakit
            </button>
            <button
              onClick={() => setTableFilter('card')}
              className={`px-2.5 py-1 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                tableFilter === 'card'
                  ? 'bg-purple-600 text-white shadow-xs'
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
              }`}
            >
              Kredi Kartı
            </button>
            <button
              onClick={() => setTableFilter('transfer')}
              className={`px-2.5 py-1 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                tableFilter === 'transfer'
                  ? 'bg-sky-600 text-white shadow-xs'
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
              }`}
            >
              EFT / Havale
            </button>
            <button
              onClick={() => setTableFilter('debt')}
              className={`px-2.5 py-1 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                tableFilter === 'debt'
                  ? 'bg-amber-600 text-white shadow-xs'
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
              }`}
            >
              Kalan Bakiye / Borç
            </button>
          </div>

          {/* Search Input */}
          <div className="relative w-full sm:w-64">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
            <input
              type="text"
              placeholder="Cihaz, müşteri veya servis no ara..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white"
            />
          </div>
        </div>

        {/* Device Rows Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-100 text-slate-700 font-bold border-b border-slate-200">
              <tr>
                <th className="py-3 px-3.5">Servis No / Tarih</th>
                <th className="py-3 px-3.5">Müşteri Bilgileri</th>
                <th className="py-3 px-3.5">Cihaz & Model</th>
                <th className="py-3 px-3.5">Yapılan İşlem / Arıza</th>
                <th className="py-3 px-3.5">Durum</th>
                <th className="py-3 px-3.5 text-right">İşçilik / Parça</th>
                <th className="py-3 px-3.5 text-right">Toplam Tutar</th>
                <th className="py-3 px-3.5 text-right">Tahsilat & Ödeme Türü</th>
                <th className="py-3 px-3.5 text-right">Kalan Borç</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {tableRows.length > 0 ? (
                tableRows.map(t => {
                  const { grandTotal, partsTotal } = calculateTicketTotal(
                    t.parts,
                    t.laborCost,
                    t.vatRate,
                    t.discount,
                    t.isVatIncluded, t.roundingAdjustment || 0
                  );
                  const split = selectedMonth === 'all' ? getTicketPaymentSplit(t) : getTicketPaymentSplitForPeriod(t, selectedMonth);
                  const paid = split.total;
                  const remaining = Math.max(0, grandTotal - getTicketPaymentSplit(t).total);
                  const tDate = (getTicketReportDate(t) || '').substring(0, 10);

                  return (
                    <tr key={t.id} className="hover:bg-indigo-50/30 transition-colors">
                      <td className="py-3 px-3.5 whitespace-nowrap">
                        <span className="font-mono font-bold text-indigo-700 block">#{t.id}</span>
                        <span className="text-[11px] text-slate-400">{formatDateTR(tDate)}</span>
                      </td>
                      <td className="py-3 px-3.5">
                        <span className="font-bold text-slate-900 block">{t.customerName}</span>
                        <span className="text-[11px] text-slate-500 font-mono">{t.customerPhone}</span>
                      </td>
                      <td className="py-3 px-3.5">
                        <span className="font-semibold text-slate-800 block">
                          {t.deviceBrand} {t.deviceModel}
                        </span>
                        <span className="text-[10px] text-slate-400">({t.deviceType})</span>
                      </td>
                      <td className="py-3 px-3.5 max-w-xs truncate">
                        {t.technicianNotes ? (
                          <span className="text-slate-800 font-medium block truncate" title={t.technicianNotes}>
                            🛠️ {t.technicianNotes}
                          </span>
                        ) : (
                          <span className="text-slate-400 italic block truncate" title={t.issueDescription}>
                            {t.issueDescription}
                          </span>
                        )}
                        {t.parts && t.parts.length > 0 && (
                          <span className="text-[10px] text-indigo-600 font-semibold block">
                            Parçalar: {t.parts.map(p => p.name).join(', ')}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-3.5 whitespace-nowrap">
                        <span
                          className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${STATUS_COLORS[t.status].bg} ${STATUS_COLORS[t.status].text} ${STATUS_COLORS[t.status].border}`}
                        >
                          {STATUS_LABELS[t.status]}
                        </span>
                      </td>
                      <td className="py-3 px-3.5 text-right whitespace-nowrap">
                        <span className="font-medium text-slate-700 block">İşçilik: {formatTL(t.laborCost || 0)}</span>
                        {partsTotal > 0 && (
                          <span className="text-[11px] text-slate-400 block">Parça: {formatTL(partsTotal)}</span>
                        )}
                      </td>
                      <td className="py-3 px-3.5 text-right whitespace-nowrap">
                        <span className="font-extrabold text-slate-900 text-sm block">
                          {formatTL(grandTotal)}
                        </span>
                      </td>
                      <td className="py-3 px-3.5 text-right whitespace-nowrap">
                        <span className="font-bold text-emerald-600 block">{formatTL(paid)}</span>
                        <div className="text-[10px] text-slate-500 flex items-center justify-end gap-1 font-medium mt-0.5">
                          {split.cash > 0 && <span className="bg-emerald-50 text-emerald-800 px-1 py-0.2 rounded border border-emerald-200">Nakit</span>}
                          {split.card > 0 && <span className="bg-purple-50 text-purple-800 px-1 py-0.2 rounded border border-purple-200">Kart</span>}
                          {split.transfer > 0 && <span className="bg-sky-50 text-sky-800 px-1 py-0.2 rounded border border-sky-200">EFT</span>}
                        </div>
                      </td>
                      <td className="py-3 px-3.5 text-right whitespace-nowrap">
                        {remaining > 0 ? (
                          <span className="font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded text-xs">
                            {formatTL(remaining)}
                          </span>
                        ) : (
                          <span className="text-emerald-700 font-bold text-xs">Borç Yok</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={9} className="py-8 text-center text-slate-400">
                    <p className="font-semibold text-sm">Bu filtreye uygun teknisyen servis kaydı bulunamadı.</p>
                    <p className="text-xs mt-1">Farklı bir ay veya teknisyen seçebilirsiniz.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
